"use client";
import { Button } from "@/components/shared/ui/button";
import { Checkbox } from "@/components/shared/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/components/shared/ui/dialog";
import { EmailInput } from "@/components/shared/ui/email-input";
import { Input } from "@/components/shared/ui/input";
import { Label } from "@/components/shared/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/shared/ui/select";
import { Textarea } from "@/components/shared/ui/text-area";
import { ToastAction } from "@/components/shared/ui/toast";
import {
    convertToMockOrderAction,
    findOrCreateCartAndSetCookie,
    getDeliveryDatesAction,
    getEmailContactListAction,
    revertMockToCartAction
} from "@/lib/cart/cart-actions";
import {
    ShopDeliveryDate,
    ShopEmailContact,
    ShopEmailContactList
} from "@/lib/checkout/checkout-types";
import { cn } from "@/lib/library";
import { useToast } from "@/state-management/hooks/use-toast";
import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useState } from "react";

export default function MockDialog({
    cartID,
    customerID,
    onSuccess,
    defaultValues
}: {
    cartID: number;
    customerID: number;
    onSuccess?: () => void;
    defaultValues?: {
        PONum?: string;
        deliveryDate?: ShopDeliveryDate;
        orderMemo?: string;
        emailNote?: string;
        sendEmail?: boolean;
        contacts?: ShopEmailContact[];
    };
}) {
    const [open, setOpen] = useState(false);
    const [PONum, setPONum] = useState(defaultValues?.PONum || "");
    const [deliveryDate, setDeliveryDate] = useState<ShopDeliveryDate | undefined>(
        defaultValues?.deliveryDate
    );
    const [deliveryDates, setDeliveryDates] = useState<ShopDeliveryDate[]>([]);
    const [orderMemo, setOrderMemo] = useState(defaultValues?.orderMemo || "");
    const [emailNote, setEmailNote] = useState(defaultValues?.emailNote || "");
    const [sendEmail, setSendEmail] = useState(defaultValues?.sendEmail || false);
    const [contacts, setContacts] = useState<ShopEmailContactList[]>([]);
    const [selectedContacts, setSelectedContacts] = useState<ShopEmailContact[]>(
        defaultValues?.contacts || []
    );
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        if (open) {
            setLoading(true);
            Promise.all([getEmailContactListAction(customerID), getDeliveryDatesAction(customerID)])
                .then(([fetchedContacts, fetchedDates]) => {
                    setContacts(fetchedContacts);
                    setDeliveryDates(fetchedDates);

                    if (defaultValues?.deliveryDate) {
                        setDeliveryDate(defaultValues.deliveryDate);
                    } else if (fetchedDates.length > 0) {
                        setDeliveryDate(fetchedDates[0]);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Failed to fetch data:", error);
                    setLoading(false);
                });
        }
    }, [open, customerID, defaultValues]);

    const handleConfirm = async () => {
        const success = await convertToMockOrderAction(cartID, {
            PONumber: PONum,
            Memo: orderMemo,
            deliveryDate: deliveryDate,
            emailNote: sendEmail ? emailNote : "",
            sendEmail,
            selectedContacts: sendEmail ? selectedContacts : []
        });
        setOpen(false);
        resetForm();

        if (success) {
            await findOrCreateCartAndSetCookie();
            toast({
                variant: "default",
                title: "Cart converted to mock order successfully!",
                description: (
                    <span>
                        View the mock order here:{" "}
                        <a
                            href="/mock-orders"
                            className="font-medium text-blue-500 underline underline-offset-4"
                        >
                            Mock Orders page
                        </a>
                    </span>
                ),
                action: (
                    <ToastAction
                        altText="undo"
                        onClick={async () => {
                            await revertMockToCartAction(cartID);
                            await findOrCreateCartAndSetCookie();
                        }}
                    >
                        Undo
                    </ToastAction>
                )
            });

            if (onSuccess) {
                onSuccess();
            }
        } else {
            toast({
                variant: "destructive",
                title: "Failed to convert cart to mock order."
            });
        }
    };

    const resetForm = () => {
        setPONum(defaultValues?.PONum || "");
        setDeliveryDate(
            deliveryDates.length > 0 && deliveryDates[0] ? deliveryDates[0] : undefined
        );
        setOrderMemo(defaultValues?.orderMemo || "");
        setEmailNote("");
        setSendEmail(false);
        setSelectedContacts([]);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(newOpen) => {
                setOpen(newOpen);
                if (!newOpen) resetForm();
            }}
        >
            <DialogTrigger asChild>
                <Button variant={"secondary"} className="h-fit p-2">
                    Create Mock Order
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Create Mock Order</DialogTitle>
                    <ul className="list-disc pl-4">
                        <li>
                            Mock orders are visible to all users on the{" "}
                            <a
                                href="/mock-orders"
                                className="font-medium text-blue-500 underline underline-offset-4"
                            >
                                Mock Orders
                            </a>{" "}
                            page
                        </li>
                        <li>Mock Orders cannot be directly edited after creation</li>
                        <li>
                            You can pull back a Mock Order from the Mock Orders page to make changes
                        </li>
                    </ul>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="deliveryDate" className="text-left">
                                Proposed Delivery Date
                            </Label>
                            <Select
                                value={deliveryDate?.date}
                                onValueChange={(value) => {
                                    const date = deliveryDates.find(
                                        (deliveryDate) => deliveryDate.date === value
                                    );
                                    setDeliveryDate(date);
                                }}
                                disabled={loading || deliveryDates.length === 0}
                            >
                                <SelectTrigger id="deliveryDate">
                                    <SelectValue placeholder="Select delivery date" />
                                </SelectTrigger>
                                <SelectContent>
                                    {deliveryDates.map((deliveryDate) => (
                                        <SelectItem
                                            key={deliveryDate.date}
                                            value={deliveryDate.date}
                                            defaultChecked={
                                                defaultValues?.deliveryDate?.date ===
                                                deliveryDate.date
                                            }
                                        >
                                            {formatInTimeZone(
                                                deliveryDate.date,
                                                "UTC",
                                                "EEEE, MMM. do"
                                            )}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="poNumber" className="text-left">
                                PO Number
                            </Label>
                            <Input
                                id="poNumber"
                                type="text"
                                value={PONum}
                                onChange={(e) => setPONum(e.target.value)}
                                placeholder="Enter PO number..."
                            />
                        </div>
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="orderMemo" className="text-left">
                            Order Memo
                        </Label>
                        <Textarea
                            id="orderMemo"
                            value={orderMemo}
                            onChange={(e) => setOrderMemo(e.target.value)}
                            placeholder="Add order memo..."
                            className="col-span-3"
                        />
                    </div>

                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="sendEmail"
                            checked={sendEmail}
                            onCheckedChange={(checked) => {
                                setSendEmail(checked === true);
                                if (!checked) {
                                    setSelectedContacts([]);
                                }
                            }}
                        />
                        <Label htmlFor="sendEmail">Send Email</Label>
                    </div>

                    <div className={cn("grid gap-2", !sendEmail && "opacity-50")}>
                        <Label htmlFor="contacts" className="text-left">
                            Select Contacts
                        </Label>
                        <EmailInput
                            defaultValue={[]}
                            contactGroups={contacts}
                            onEmailsChange={(emails) => setSelectedContacts(emails)}
                            disabled={!sendEmail || loading}
                        />
                    </div>

                    <div className={cn("grid gap-2", !sendEmail && "opacity-50")}>
                        <Label htmlFor="note" className="text-left">
                            Email Note
                        </Label>
                        <Textarea
                            id="note"
                            value={emailNote}
                            onChange={(e) => setEmailNote(e.target.value)}
                            placeholder="Add a message to include in the email..."
                            className="col-span-3"
                            disabled={!sendEmail}
                        />
                    </div>
                </div>
                <DialogFooter className="flex justify-between sm:justify-between">
                    <Button variant="outline" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleConfirm}
                        disabled={sendEmail && selectedContacts.length === 0}
                    >
                        Confirm
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
